import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CreditCard, Wallet, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import CircuitField from "@/components/CircuitField";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

function ProjectPayCard({ testid, buttonTestid, href, title, text, buttonLabel, featured = false, fullWidth = false }) {
    return (
        <Reveal className={fullWidth ? "md:col-span-2" : ""}>
            <div
                data-testid={testid}
                className={`flex h-full flex-col gap-6 p-8 sm:flex-row sm:items-center ${
                    featured ? "glow-border bg-raised" : "border border-white/10 bg-surface transition-[border-color] duration-500 hover:border-white/30"
                }`}
            >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/10 text-neon">
                    <ShieldCheck className="h-5 w-5" />
                </span>
                <div className="flex-1">
                    <h3 className="font-display text-lg font-bold">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-400">{text}</p>
                </div>
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={buttonTestid}
                    className={`group flex shrink-0 items-center justify-center gap-2 px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase transition-shadow duration-300 ${
                        featured
                            ? "bg-neon text-void hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                            : "border border-neon/70 text-neon transition-[background-color,color,box-shadow] hover:bg-neon hover:text-void hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                    }`}
                >
                    {buttonLabel}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
            </div>
        </Reveal>
    );
}

export default function Pricing() {
    const { pricingPlans, VENMO, DEPOSIT_LINK, BALANCE_LINK } = useSiteContent();
    return (
        <div data-testid="pricing-page">
            <SEO
                title="Pricing | Affordable Websites from $100 — Camarca Digital Designs"
                description="Simple, affordable pricing for small business websites (from $100), logo design ($60), branding kits ($70), Facebook page setup ($50) and website updates ($60). Pay online securely."
                path="/pricing"
            />

            <div className="relative overflow-hidden">
                <CircuitField className="pointer-events-none absolute inset-0 h-full w-full opacity-40" />
                <header className="relative mx-auto max-w-7xl px-6 pb-16 pt-40 lg:px-10">
                    <Reveal>
                        <SectionTag>Pricing</SectionTag>
                        <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl">
                            Honest pricing.{" "}
                            <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">No agency markup.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl leading-relaxed text-zinc-400 md:text-lg">
                            Professional design packages priced for real businesses of any size — start with a 40% deposit, pay the rest when it's done.
                        </p>
                    </Reveal>
                </header>
            </div>

            <section className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {pricingPlans.map((p, i) => (
                        <Reveal key={p.name} delay={(i % 3) * 0.08}>
                            <div
                                data-testid={`pricing-card-${i}`}
                                className={`relative flex h-full flex-col p-8 ${
                                    p.featured
                                        ? "glow-border bg-raised"
                                        : "border border-white/10 bg-surface transition-[border-color] duration-500 hover:border-white/30"
                                }`}
                            >
                                {p.featured && (
                                    <span className="absolute -top-3 left-8 bg-gradient-to-r from-neon to-deep px-3 py-1 font-mono text-[10px] font-bold tracking-[0.25em] uppercase text-void">
                                        Most Popular
                                    </span>
                                )}
                                <h2 className="font-display text-lg font-bold">{p.name}</h2>
                                <p className="mt-4 font-display text-5xl font-black tracking-tight text-white">
                                    {p.price}
                                </p>
                                <p className="mt-5 flex-1 text-sm leading-relaxed text-zinc-400">{p.description}</p>
                                <div className="mt-8 space-y-4">
                                    {p.stripe ? (
                                        <>
                                            <a
                                                href={p.stripe}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`pay-link-${i}`}
                                                className={`group flex items-center justify-center gap-2 px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase transition-shadow duration-300 ${
                                                    p.featured
                                                        ? "bg-neon text-void hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                                                        : "border border-white/20 text-white hover:border-neon hover:text-neon"
                                                } transition-[border-color,color]`}
                                            >
                                                Pay Online
                                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                            </a>
                                            <Link
                                                to="/contact"
                                                data-testid={`pricing-quote-link-${i}`}
                                                className="group flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 transition-colors duration-300 hover:text-neon"
                                            >
                                                or start a project first
                                                <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                                            </Link>
                                        </>
                                    ) : (
                                        <Link
                                            to="/contact"
                                            data-testid={`pricing-quote-link-${i}`}
                                            className="group flex items-center justify-center gap-2 border border-neon/70 px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color,box-shadow] duration-300 hover:bg-neon hover:text-void hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                                        >
                                            Start a Project
                                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-16">
                    <div data-testid="pricing-note" className="border border-white/10 bg-surface p-6 md:p-8">
                        <p className="font-mono text-xs leading-relaxed tracking-wider text-zinc-400">
                            <span className="text-neon">PAYMENT TERMS —</span> 40% down to start,
                            the rest due when your website is complete. E-commerce websites with heavier
                            integration typically land closer to $600. For custom projects, submit the quote
                            form first so the correct scope and payment can be confirmed.
                        </p>
                        <p className="mt-5 font-mono text-xs leading-relaxed tracking-wider text-zinc-400">
                            <span className="text-neon">HOSTING & MAINTENANCE —</span> Websites built by Camarca
                            Digital Designs are hosted and maintained for $25/month, billed automatically. This keeps
                            your website live, secure, and operational, and includes minor updates and support. If
                            hosting payments lapse, the website may be taken offline until payments resume. You always
                            own your domain and your content — take it elsewhere anytime, no lock-in.
                        </p>
                    </div>
                </Reveal>
            </section>

            {/* WAYS TO PAY */}
            <section data-testid="ways-to-pay" className="mx-auto max-w-7xl px-6 pt-16 lg:px-10">
                <Reveal>
                    <SectionTag>Ways to Pay</SectionTag>
                    <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-4xl">
                        Pay however works for you.
                    </h2>
                    <p className="mt-4 max-w-xl text-zinc-400">
                        Every project starts with a 40% deposit — the exact amount is confirmed with you before anything is due.
                    </p>
                </Reveal>
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    {DEPOSIT_LINK && (
                        <ProjectPayCard
                            testid="pay-card-deposit"
                            buttonTestid="pay-deposit-button"
                            href={DEPOSIT_LINK}
                            title="Pay Your Project Deposit"
                            text="Project confirmed? Lock in your start date — set the quantity to match your deposit amount (40% of your total) and check out securely by card."
                            buttonLabel="Pay Deposit"
                            featured
                            fullWidth={!BALANCE_LINK}
                        />
                    )}
                    {BALANCE_LINK && (
                        <ProjectPayCard
                            testid="pay-card-balance"
                            buttonTestid="pay-balance-button"
                            href={BALANCE_LINK}
                            title="Pay Your Final Balance"
                            text="Website complete and approved? Set the quantity to match your remaining balance and check out securely by card."
                            buttonLabel="Pay Balance"
                        />
                    )}
                    <Reveal>
                        <div data-testid="pay-card-stripe" className="flex h-full gap-5 border border-white/10 bg-surface p-8">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/10 text-neon">
                                <CreditCard className="h-5 w-5" />
                            </span>
                            <div>
                                <h3 className="font-display text-lg font-bold">Card — Stripe</h3>
                                <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                                    Secure online checkout with any major card. Use the Pay Online buttons above for fixed-price services, or I'll send a link for your exact amount.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                    {VENMO && (
                        <Reveal delay={0.1}>
                            <a
                                href={`https://venmo.com/u/${VENMO}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="pay-card-venmo"
                                className="group flex h-full gap-5 border border-white/10 bg-surface p-8 transition-[border-color] duration-500 hover:border-neon/50"
                            >
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/10 text-neon">
                                    <Wallet className="h-5 w-5" />
                                </span>
                                <div>
                                    <h3 className="font-display text-lg font-bold">Venmo</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                                        Send to <span className="text-neon">@{VENMO}</span> with your business name in the note. Quick and done.
                                    </p>
                                </div>
                                <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 self-start text-zinc-600 transition-[color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-neon" />
                            </a>
                        </Reveal>
                    )}
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                <Reveal className="flex flex-wrap items-center justify-between gap-8">
                    <div>
                        <h2 className="font-display text-2xl font-bold md:text-3xl">Custom project? Bigger idea?</h2>
                        <p className="mt-3 max-w-lg text-zinc-400">Send the details and I'll confirm scope and pricing before anything starts.</p>
                    </div>
                    <Link
                        to="/contact"
                        data-testid="pricing-bottom-cta"
                        className="group flex items-center gap-3 border border-neon/70 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color,box-shadow] duration-300 hover:bg-neon hover:text-void hover:shadow-[0_0_25px_rgba(0,240,255,0.4)]"
                    >
                        Request a Quote
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}
